import * as React from 'react'
import { useEffect, useState } from 'react'
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  NumberInput,
  BooleanInput,
  DateInput,
  Toolbar,
  SaveButton,
  SimpleShowLayout,
  FunctionField,
  ReferenceField,
  TextField,
  useNotify,
  useRefresh,
  useRedirect,
  useDataProvider,
  required,
  AutocompleteInput,
  ReferenceInput,
  useEditContext,
  Loading
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { Box, Typography, Divider, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'
import { formatDateToMMDDYYYY, fromTimestamp } from '../../utils/helpers'
import PhoneNumberInput from '../fields/PhoneNumberInput'

// ---------------------------------------------------------------------------
// Date helpers — mirrors provider-portal/src/components/KitRequests/KitRequestEdit.js
// ---------------------------------------------------------------------------

/**
 * Strips an ISO timestamp to a YYYY-MM-DD string suitable for DateInput.
 * Handles both full ISO strings ("2000-09-01T00:00:00.000Z") and plain
 * date strings ("2000-09-01") gracefully.
 */
const formatDateValue = (value) => {
  if (!value) return ''
  return String(value).slice(0, 10)
}

/**
 * Re-encodes the date picker's plain YYYY-MM-DD back to a UTC-midnight ISO
 * timestamp on submit, so editing here doesn't silently downgrade the stored
 * format. Matches KitRequestForm.js (create) and provider-portal's
 * KitRequestEdit.js. Only for patientDob / dateOfVasectomy — the fulfillment
 * dates are written as plain YYYY-MM-DD by convertKitRequest.js and
 * kitRequestDates.js, so they stay date-only.
 */
const parseDateValue = (value) => {
  if (!value) return value
  return new Date(value).toISOString()
}


const isFutureDate = (dateString) => {
  if (!dateString) return false
  const selected = new Date(dateString)
  const today = new Date()
  today.setHours(23, 59, 59, 999)
  return selected > today
}

const calculateAge = (dobString) => {
  if (!dobString) return 0
  const dob = new Date(dobString)
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--
  }
  return age
}

// Field-level validators
const validatePatientDob = (value) => {
  if (!value) return undefined
  if (isFutureDate(value)) return 'Date cannot be in the future'
  if (calculateAge(value) < 16) return 'Patient must be at least 16 years old'
  return undefined
}

// dateOfVasectomy: future dates are intentionally allowed (mirrors portal comment)
const validatePastOrPresentDate = (value) => {
  if (!value) return undefined
  if (isFutureDate(value)) return 'Date cannot be in the future'
  return undefined
}

// ---------------------------------------------------------------------------
// Server-side Error Handler — mirrors provider-portal/KitRequestEdit.js
// Must be rendered inside SimpleForm so react-hook-form context is available.
// ---------------------------------------------------------------------------
const ServerErrorHandler = ({ serverErrors }) => {
  const { setError } = useFormContext()

  useEffect(() => {
    if (serverErrors && Object.keys(serverErrors).length > 0) {
      // Small timeout ensures react-hook-form is fully ready
      setTimeout(() => {
        Object.entries(serverErrors).forEach(([field, msg]) => {
          setError(field, { type: 'manual', message: msg }, { shouldFocus: true })
        })
      }, 100)
    }
  }, [serverErrors, setError])

  return null
}

const KitRequestTitle = () => {
  return <span>Edit Kit Request</span>
}

const KitRequestEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton label="Save" />
  </Toolbar>
)

const EditContent = ({ serverErrors }) => {
  const { record, isLoading } = useEditContext()
  if (isLoading || !record) return <Loading />

  return (
    <SimpleForm className="form-layout" toolbar={<KitRequestEditToolbar />}>
      <ServerErrorHandler serverErrors={serverErrors} />
      {/* Top Summary Section - Read Only */}
      <Box sx={{ flexGrow: 1, border: '2px solid black', padding: '16px', mb: 4, width: '100%' }}>
        <Grid container spacing={1}>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <TextField label="Status" source="requestStatus" />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <FunctionField
                label="Patient Name"
                render={(record) => `${record.patientFirstName || ''} ${record.patientLastName || ''}`}
              />
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <ReferenceField link="show" label="Provider" source="providerId" reference="providers">
                <TextField source="displayName" />
              </ReferenceField>
            </SimpleShowLayout>
          </Grid>
          <Grid item xs={3}>
            <SimpleShowLayout spacing={1} className="details-page-layout">
              <FunctionField label="Created At" render={(record) => fromTimestamp(record.createdAt) || '—'} />
            </SimpleShowLayout>
          </Grid>
        </Grid>
      </Box>

      <Typography variant="h6" gutterBottom>Status</Typography>
      <SelectInput source="requestStatus" choices={[
        { id: 'pending', name: 'Pending' },
        { id: 'convertedToOrder', name: 'Converted to Order' },
        { id: 'failedToCreate', name: 'Failed to Create' },
        { id: 'canceled', name: 'Canceled' },
      ]} fullWidth validate={required()} />

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Patient Information</Typography>
      <Box display="flex" gap={2} width="100%">
        <TextInput source="patientFirstName" label="First Name" fullWidth />
        <TextInput source="patientLastName" label="Last Name" fullWidth />
      </Box>
      <Box display="flex" gap={2} width="100%">
        <TextInput source="email" label="Email" fullWidth />
        <PhoneNumberInput source="phoneNumber" label="Phone Number" fullWidth />
      </Box>
      <Box display="flex" gap={2} width="100%">
        <DateInput
          source="patientDob"
          label="Date of Birth"
          format={formatDateValue}
          parse={parseDateValue}
          validate={validatePatientDob}
          fullWidth
        />
        <DateInput
          source="dateOfVasectomy"
          label="Date of Vasectomy"
          format={formatDateValue}
          parse={parseDateValue}
          fullWidth
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Shipping Information</Typography>
      <Box display="flex" gap={2} width="100%">
        <TextInput source="shippingAddress1" label="Address 1" fullWidth validate={required()} />
        <TextInput source="shippingAddress2" label="Address 2" fullWidth />
      </Box>
      <Box display="flex" gap={2} width="100%">
        <TextInput source="shippingCity" label="City" fullWidth validate={required()} />
        <TextInput source="shippingState" label="State" fullWidth validate={required()} />
        <TextInput source="shippingZip" label="ZIP" fullWidth validate={required()} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Provider & Hierarchy</Typography>
      <Box display="flex" gap={2} width="100%">
        <ReferenceInput source="providerGroupId" reference="provider-groups">
          <AutocompleteInput fullWidth optionText="name" label="Provider Group" />
        </ReferenceInput>
        <ReferenceInput source="providerLocationId" reference="provider-locations" filter={{ providerGroupId: record.providerGroupId }}>
          <AutocompleteInput fullWidth optionText="locationName" label="Provider Location" />
        </ReferenceInput>
        <ReferenceInput source="providerId" reference="providers">
          <AutocompleteInput fullWidth optionText={'displayName'} label="Provider" optionValue="providerId" />
        </ReferenceInput>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Financials & Contracts</Typography>
      <ReferenceInput source="providerContractId" reference="provider-contracts" filter={{ providerGroupId: record.providerGroupId }}>
        <AutocompleteInput fullWidth optionText={(c) => `${c.startDate} to ${c.endDate || 'Present'}`} label="Active Contract" />
      </ReferenceInput>

      <Box display="flex" gap={2} width="100%">
        <Box flex={1}>
          <Typography variant="subtitle2">Shipped Kit Financials</Typography>
          <SelectInput source="shippedFinancialStatus" choices={[
            { id: 'none', name: 'None' },
            { id: 'invoiced', name: 'Invoiced' },
            { id: 'paid', name: 'Paid' },
          ]} />
          <Box>
            <NumberInput source="providerShippedAmount" label="Provider Shipped Amount" />
          </Box>
          <Box>
            <NumberInput source="customerAmount" label="Patient Charged" />
          </Box>
          <Box>
            <TextInput source="shippedInvoiceId" label="Shipped Invoice ID" disabled />
          </Box>
        </Box>
        <Box flex={1}>
          <Typography variant="subtitle2">Tested Kit Financials</Typography>
          <SelectInput source="testedFinancialStatus" choices={[
            { id: 'none', name: 'None' },
            { id: 'invoiced', name: 'Invoiced' },
            { id: 'paid', name: 'Paid' },
          ]} />
          <Box>
            <NumberInput source="providerTestedAmount" label="Provider Tested Amount" />
          </Box>
          <Box>
            <TextInput source="testedInvoiceId" label="Tested Invoice ID" disabled />
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Fulfillment & Tracking</Typography>
<Box display="flex" gap={2} width="100%">
        <TextInput source="orderId" label="Shopify Order ID" fullWidth />
        <TextInput source="shopifyOrderNumber" label="Shopify Order Number" fullWidth />
        <TextInput source="kitId" label="MFH Kit ID" fullWidth />
      </Box>
      <Box display="flex" gap={2} width="100%">
        <DateInput
          source="convertDate"
          label="Convert Date"
          format={formatDateValue}
          validate={validatePastOrPresentDate}
          fullWidth
        />
        <DateInput
          source="targetConvertDate"
          label="Target Convert Date"
          format={formatDateValue}
          fullWidth
        />
        <DateInput
          source="shippedDate"
          label="Shipped Date"
          format={formatDateValue}
          validate={validatePastOrPresentDate}
          fullWidth
        />
        <DateInput
          source="testedDate"
          label="Tested Date"
          format={formatDateValue}
          validate={validatePastOrPresentDate}
          fullWidth
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>Other</Typography>
      <TextInput source="note" label="Notes" multiline fullWidth />
      <BooleanInput source="agreedToTerms" label="Agreed to Terms" />
      <SelectInput source="notificationMethod" choices={[
        { id: 'Email', name: 'Email' },
        { id: 'Text', name: 'SMS' },
        { id: 'Both', name: 'Both' },
      ]} fullWidth />

    </SimpleForm>
  )
}

export const KitRequestEdit = () => {
  const notify = useNotify()
  const refresh = useRefresh()
  const redirect = useRedirect()
  const dataProvider = useDataProvider()
  const [serverErrors, setServerErrors] = useState(null)
  const [addressConfirmation, setAddressConfirmation] = useState(null)

  const handleError = (error) => {
    const responseData = error?.response?.data
    if (responseData?.fieldErrors && Object.keys(responseData.fieldErrors).length > 0) {
      setServerErrors(responseData.fieldErrors)
    }
    const errorMessage =
      responseData?.message ||
      error?.body?.message ||
      'Failed to update kit request'
    notify(errorMessage, { type: 'error' })
  }

  const closeAddressConfirmation = () => {
    setAddressConfirmation(null)
  }

  // The API recomputes targetConvertDate whenever the Date of Vasectomy changes, so the
  // stored value can differ from what the form submitted. Fold that into the save
  // confirmation — one toast, since react-admin queues notifications one at a time.
  const buildSaveMessage = (data) =>
    data?.targetConvertDateRecalculated
      ? `Kit request updated. Target Convert Date recalculated to ${formatDateToMMDDYYYY(data.targetConvertDate)} from the new Date of Vasectomy.`
      : 'Kit request updated successfully'

  // Retry the update directly via the data provider with an explicit override
  // flag, bypassing SimpleForm's controlled submit so the admin doesn't have to
  // re-enter or resubmit anything. Used both when Google couldn't verify the
  // address at all, and when the admin wants to keep their entry over a suggestion.
  const useAddressAsEntered = async () => {
    if (!addressConfirmation) return
    const { resource, id, data, previousData } = addressConfirmation
    setAddressConfirmation(null)
    try {
      const result = await dataProvider.update(resource, {
        id,
        data: { ...data, addressConfirmationAccepted: true },
        previousData,
      })
      setServerErrors(null)
      notify(buildSaveMessage(result?.data), { type: 'success' })
      refresh()
    } catch (error) {
      handleError(error)
    }
  }

  const useSuggestedAddress = async () => {
    if (!addressConfirmation?.suggestedAddress) return
    const { resource, id, data, previousData, suggestedAddress } = addressConfirmation
    setAddressConfirmation(null)
    try {
      const result = await dataProvider.update(resource, {
        id,
        data: { ...data, ...suggestedAddress, addressConfirmationAccepted: true },
        previousData,
      })
      setServerErrors(null)
      notify(buildSaveMessage(result?.data), { type: 'success' })
      refresh()
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <>
      <Edit
        title={<KitRequestTitle />}
        mutationMode="pessimistic"
        mutationOptions={{
          // Overriding onSuccess disables react-admin's default success side effects,
          // so both are reproduced here — the toast (with the target-convert-date note
          // folded in) and the redirect back to the list.
          onSuccess: (data) => {
            setServerErrors(null)
            notify(buildSaveMessage(data), { type: 'info' })
            redirect('list', 'kit-requests')
          },
          onError: (error, variables) => {
            const responseData = error?.response?.data
            if (responseData?.requiresAddressConfirmation) {
              setAddressConfirmation({
                message: responseData?.message,
                enteredAddress: responseData?.enteredAddress || {},
                suggestedAddress: responseData?.suggestedAddress || null,
                unverified: Boolean(responseData?.addressUnverified),
                resource: variables?.resource,
                id: variables?.id,
                data: variables?.data,
                previousData: variables?.previousData,
              })
              return
            }
            handleError(error)
          },
        }}
      >
        <EditContent serverErrors={serverErrors} />
      </Edit>

      <Dialog open={Boolean(addressConfirmation)} onClose={closeAddressConfirmation} maxWidth="sm" fullWidth>
        <DialogTitle>
          {addressConfirmation?.unverified ? "We Couldn't Verify This Address" : 'Confirm Shipping Address'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {addressConfirmation?.message ||
              (addressConfirmation?.unverified
                ? "We weren't able to verify this address. It may be a new address that isn't in postal records yet. You can go back and double-check it, or save it exactly as entered."
                : 'We found a recommended shipping address. Please confirm to continue.')}
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {addressConfirmation?.unverified ? 'Address entered' : 'You entered'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: addressConfirmation?.suggestedAddress ? 2 : 0 }}>
            {[
              addressConfirmation?.enteredAddress?.shippingAddress1,
              addressConfirmation?.enteredAddress?.shippingAddress2,
              `${addressConfirmation?.enteredAddress?.shippingCity || ''}, ${addressConfirmation?.enteredAddress?.shippingState || ''} ${addressConfirmation?.enteredAddress?.shippingZip || ''}`.trim(),
            ].filter(Boolean).join('\n')}
          </Typography>

          {addressConfirmation?.suggestedAddress && (
            <>
              <Typography variant="subtitle2">Suggested address</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {[
                  addressConfirmation.suggestedAddress?.shippingAddress1,
                  addressConfirmation.suggestedAddress?.shippingAddress2,
                  `${addressConfirmation.suggestedAddress?.shippingCity || ''}, ${addressConfirmation.suggestedAddress?.shippingState || ''} ${addressConfirmation.suggestedAddress?.shippingZip || ''}`.trim(),
                ].filter(Boolean).join('\n')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {addressConfirmation?.unverified ? (
            <>
              <Button onClick={closeAddressConfirmation}>Go back and edit</Button>
              <Button variant="contained" onClick={useAddressAsEntered}>Use address as entered</Button>
            </>
          ) : (
            <>
              <Button onClick={useAddressAsEntered}>Keep entered address</Button>
              <Button variant="contained" onClick={useSuggestedAddress}>Use suggested address</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default KitRequestEdit
